"use client"

import { useHotkeys } from "@mantine/hooks"
import { usePathname } from "next/navigation"
import { useTranslations } from "next-intl"
import { type ComponentProps, useEffect, useState } from "react"
import { Button } from "~/components/legacy/common/button"
import { Link } from "~/components/legacy/common/link"
import { Stack } from "~/components/legacy/common/stack"
import { SearchIcon } from "~/components/ui/icons"
import { ThemeSwitcher } from "~/components/legacy/web/theme-switcher"
import { Hamburger } from "~/components/legacy/web/ui/hamburger"
import { Logo } from "~/components/legacy/web/ui/logo"
import { NavLink } from "~/components/legacy/web/ui/nav-link"
import { UserMenu } from "~/components/web/user-menu"
import { browseNavigationLinks } from "~/config/navigation-links"
import { useSearch } from "~/contexts/search-context"
import { cx } from "~/lib/legacy-utils"

const Header = ({ className, ...props }: ComponentProps<"div">) => {
  const pathname = usePathname()
  const search = useSearch()
  const t = useTranslations()
  const [isNavOpen, setNavOpen] = useState(false)

  // Close the mobile navigation when the user presses the "Escape" key
  useHotkeys([["Escape", () => setNavOpen(false)]])

  // Close the mobile navigation when the user navigates to a new page
  useEffect(() => setNavOpen(false), [pathname])

  const navLinks = browseNavigationLinks.map(({ href, labelKey }) => ({
    href,
    label: t(labelKey),
  }))

  return (
    <header
      className={cx(
        "fixed top-(--header-top) inset-x-0 z-50 bg-background border-b border-border",
        className,
      )}
      data-state={isNavOpen ? "open" : "close"}
      {...props}
    >
      <div className="mx-auto w-full max-w-5xl px-fluid-md">
        <div className="flex items-center py-3.5 gap-4 text-sm h-(--header-height) md:gap-6 lg:gap-8">
          <Stack size="sm" wrap={false} className="min-w-0">
            <button
              type="button"
              onClick={() => setNavOpen(!isNavOpen)}
              className="block -m-1 -ml-1.5 lg:hidden"
              aria-label={t("navigation.toggle_menu")}
              aria-expanded={isNavOpen}
            >
              <Hamburger className="size-7" />
            </button>

            <Logo className="min-w-0" />
          </Stack>

          <nav className="flex items-center gap-x-4 gap-y-0.5 flex-1 max-lg:hidden">
            {navLinks.map(({ href, label }) => (
              <NavLink key={href} href={href}>
                {label}
              </NavLink>
            ))}
          </nav>

          <Stack size="sm" wrap={false} className="justify-end max-lg:grow">
            <Button size="sm" variant="ghost" className="p-1 text-base" onClick={search.open}>
              <SearchIcon />
            </Button>

            <Button size="sm" variant="ghost" className="p-1 -ml-1 text-base max-sm:hidden" asChild>
              <ThemeSwitcher />
            </Button>

            <UserMenu signedOutClassName="max-lg:hidden" />

            <Button size="sm" variant="primary" asChild>
              <Link href="/submit">Submit a tool</Link>
            </Button>
          </Stack>
        </div>

        <nav
          aria-hidden={!isNavOpen || undefined}
          className={cx(
            "absolute top-full inset-x-0 h-[calc(100dvh-var(--header-top)-var(--header-height))] -mt-px flex flex-col overflow-y-auto overscroll-contain bg-background px-6 py-4 transition-opacity lg:hidden",
            isNavOpen ? "visible opacity-100" : "invisible opacity-0 pointer-events-none",
          )}
        >
          {navLinks.map(({ href, label }) => (
            <NavLink
              key={href}
              href={href}
              onClick={() => setNavOpen(false)}
              className="rounded-lg px-1 py-3 text-base"
              isPadded={false}
            >
              {label}
            </NavLink>
          ))}

          <NavLink
            href="/auth/login"
            onClick={() => setNavOpen(false)}
            className="rounded-lg px-1 py-3 text-base"
            isPadded={false}
          >
            {t("navigation.sign_in")}
          </NavLink>
        </nav>
      </div>
    </header>
  )
}

export { Header }
